<template>
  <div>
    <b-card>
      <TitleTable titleTable="IV Add-ons" iconTable="fas fa-plus-circle" />
      <div class="m-1">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <treeselect
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="w-25" />
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <GoBack class="mr-2" />
              <b-button v-if="$can('store', 'iv-addon')" variant="primary" @click="openAdd">Add</b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No IV add-ons found">
        <template #cell(facility_id)="data">
          {{ facilityDisplay(data.item) }}
        </template>
        <template #cell(price)="data">
          {{ displayPrice(data.value) }}
        </template>
        <template #cell(active)="data">
          <b-badge :variant="data.value ? 'success' : 'secondary'">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="$can('update', 'iv-addon')"
            size="sm"
            variant="outline-primary"
            class="mr-1"
            @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
          <b-button v-if="$can('destroy', 'iv-addon')" size="sm" variant="outline-danger" @click="remove(data.item)">
            <i class="fas fa-trash-alt"></i>
          </b-button>
        </template>
      </b-table>
      <PaginationTable
        :dataMeta="dataMeta"
        :totalUsers="totalUsers"
        :perPage="perPage"
        :currentPage="currentPage"
        @page-changed="pageChanged" />
    </b-card>

    <IvFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="iv-addon-sidebar"
      :title="editId ? 'Edit IV add-on' : 'New IV add-on'"
      :loading="saving"
      @submit="save"
      @close="onSidebarClose">
      <b-form-group label="Name *">
        <b-form-input v-model="form.name" placeholder="Add-on name" />
      </b-form-group>
      <b-form-group label="Facility">
        <treeselect
          v-model="form.facility_id"
          :options="facilityOptions"
          :normalizer="normalizer"
          :clearable="true"
          placeholder="All facilities"
          :disabled="facilityOptionsLoading || saving" />
        <small class="text-muted d-block mt-1">Leave empty for all facilities.</small>
      </b-form-group>
      <b-form-group label="Description">
        <b-form-textarea v-model="form.description" rows="2" placeholder="Optional" />
      </b-form-group>
      <b-form-group label="Catalog price">
        <b-form-input v-model="form.price" type="number" step="0.01" min="0" />
      </b-form-group>
      <b-form-group label="Active">
        <b-form-checkbox v-model="form.active" switch />
      </b-form-group>
    </IvFormSidebar>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BBadge,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
} from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import ToastNotification from '@/components/ToastNotification'
import PaginationTable from '@/components/PaginationTable'
import IvFormSidebar from './IvFormSidebar.vue'
import ivApi from '@/core/services/api/admin/iv'
import axiosF from '@/core/services/api/admin/facilities'
import Swal from 'sweetalert2'

export default {
  name: 'TableIvAddons',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    TitleTable,
    GoBack,
    ToastNotification,
    PaginationTable,
    IvFormSidebar,
  },
  data() {
    return {
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
      items: [],
      facilityOptions: [],
      facilityOptionsLoading: false,
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'facility_id', label: 'Facility' },
        { key: 'description', label: 'Description' },
        { key: 'price', label: 'Catalog price' },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: '' },
      ],
      sidebarVisible: false,
      saving: false,
      editId: null,
      form: {
        name: '',
        facility_id: null,
        description: '',
        price: '',
        active: true,
      },
      perPage: 10,
      totalUsers: 0,
      currentPage: { page: 1 },
      dataMeta: { from: 1, to: 1, of: 1 },
      perPageOptions: [10, 25, 50, 100].map(i => ({
        id: i,
        label: `Show ${i} Entries`,
      })),
    }
  },
  mounted() {
    this.init()
  },
  watch: {
    perPage() {
      this.load(1)
    },
  },
  methods: {
    async init() {
      await this.loadFacilities()
      this.load()
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        this.facilityOptions = []
        this.$refs.toast?.danger(e?.message || 'Could not load facilities')
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    displayPrice(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? `$${n.toFixed(2)}` : value
    },
    facilityDisplay(item) {
      if (item?.facility?.name) return item.facility.name
      const id = item?.facility_id
      if (id != null && id !== '') {
        const f = this.facilityOptions.find(x => x.id === id || x.id === Number(id))
        return f ? f.name : `#${id}`
      }
      return 'All facilities'
    },
    onSidebarClose() {
      this.editId = null
    },
    openAdd() {
      this.editId = null
      this.form = {
        name: '',
        facility_id: null,
        description: '',
        price: '',
        active: true,
      }
      this.sidebarVisible = true
    },
    openEdit(item) {
      this.editId = item.id
      this.form = {
        name: item.name || '',
        facility_id: item.facility_id != null ? Number(item.facility_id) : null,
        description: item.description || '',
        price: item.price != null ? item.price : '',
        active: item.active !== false,
      }
      this.sidebarVisible = true
    },
    async load(page = 1) {
      try {
        const result = await ivApi.listAddons(this.perPage || 10, page)
        this.items = result.items || []
        this.totalUsers = result.total || 0
        this.dataMeta.of = result.total || 0
        this.dataMeta.from = result.from || 0
        this.dataMeta.to = result.to || 0
        this.currentPage.page = result.current_page || page
      } catch (e) {
        this.$refs.toast.danger(e.message)
      }
    },
    pageChanged(page) {
      this.load(page)
    },
    async save() {
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$refs.toast.danger('Name is required')
        return
      }
      this.saving = true
      try {
        const payload = {
          name: String(this.form.name).trim(),
          description: this.form.description?.trim() || null,
          price:
            this.form.price === '' || this.form.price == null
              ? null
              : Number(this.form.price),
          active: !!this.form.active,
          facility_id:
            this.form.facility_id != null && this.form.facility_id !== ''
              ? Number(this.form.facility_id)
              : null,
        }
        if (this.editId) {
          await ivApi.updateAddon(this.editId, payload)
          this.$refs.toast.success('IV add-on updated')
        } else {
          await ivApi.createAddon(payload)
          this.$refs.toast.success('IV add-on created')
        }
        this.sidebarVisible = false
        this.load(this.currentPage.page)
      } catch (e) {
        this.$refs.toast.danger(e.message || 'Could not save IV add-on')
      } finally {
        this.saving = false
      }
    },
    remove(item) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to revert this',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return
        try {
          await ivApi.deleteAddon(item.id)
          this.$refs.toast.success('IV add-on deleted')
          const page = this.currentPage.page
          if (this.items.length === 1 && page > 1) this.load(page - 1)
          else this.load(page)
        } catch (e) {
          this.$refs.toast.danger(e.message || 'Could not delete IV add-on')
        }
      })
    },
  },
}
</script>
